/**
 * This file may contain code that uses generative AI for code assistance, unit testing and/or entire functions.
 * The generative model(s) used may be a combination of GitHub Copilot, OpenAI ChatGPT or others.
 *
 * Source of truth: cds-ai-service/frontend/src/utils/lrr.js
 */

import { STEP_STATE_ACTIVE, STEP_STATE_DONE, STEP_STATE_PENDING } from './generationStepStates';

export const isInProgressLrrStatus = status =>
  typeof status === 'string' && status.trim().toLowerCase() === 'in_progress';

export const THINKING_GENERATION_SNAPSHOT = {
  steps: [
    {
      id: 'thinking',
      labelKey: 'command_intelligence_reasoning_thinking',
      state: STEP_STATE_ACTIVE,
    },
  ],
  toolCount: 0,
  progressMessage: '',
};

export const hasGenerationSnapshot = snapshot =>
  Boolean(snapshot && Array.isArray(snapshot.steps) && snapshot.steps.length > 0);

export const getLrrProgressMessageDescriptor = lrr => {
  if (!lrr || typeof lrr !== 'object') {
    return {};
  }

  const prog = lrr.progress && typeof lrr.progress === 'object' ? lrr.progress : {};
  const completedTurns = Number(prog.completedTurns);
  const totalTurns = Number(prog.totalTurns);
  const toolsCalled = Array.isArray(prog.toolsCalled)
    ? prog.toolsCalled.filter(toolName => typeof toolName === 'string' && toolName.trim() !== '')
    : [];
  const elapsedMs = Number(prog.elapsedMs);
  const elapsedSeconds = Number.isFinite(elapsedMs) && elapsedMs > 0 ? Math.max(1, Math.floor(elapsedMs / 1000)) : null;

  if (Number.isFinite(completedTurns) && Number.isFinite(totalTurns) && totalTurns > 0) {
    if (toolsCalled.length > 0) {
      return {
        progressMessageKey: 'command_intelligence_generation_turn_completed_with_tools',
        progressMessageValues: {
          turn: completedTurns,
          total: totalTurns,
          tools: toolsCalled.join(', '),
        },
      };
    }

    if (elapsedSeconds !== null) {
      return {
        progressMessageKey: 'command_intelligence_generation_turn_completed_elapsed',
        progressMessageValues: {
          turn: completedTurns,
          total: totalTurns,
          seconds: elapsedSeconds,
        },
      };
    }

    return {
      progressMessageKey: 'command_intelligence_generation_turn_completed',
      progressMessageValues: {
        turn: completedTurns,
        total: totalTurns,
      },
    };
  }

  if (typeof lrr.message === 'string' && lrr.message.trim() !== '') {
    return { progressMessage: lrr.message.trim(), isServerMessage: true };
  }

  return {};
};

export const parseLrrPollSnapshot = lrr => {
  const status = typeof lrr?.status === 'string' ? lrr.status : '';
  const progress = lrr?.progress && typeof lrr.progress === 'object' ? lrr.progress : {};
  const completedTurns = Number.isFinite(Number(progress.completedTurns)) ? Number(progress.completedTurns) : null;
  const totalTurns = Number.isFinite(Number(progress.totalTurns)) ? Number(progress.totalTurns) : null;
  const toolsCalled = Array.isArray(progress.toolsCalled)
    ? progress.toolsCalled.filter(toolName => typeof toolName === 'string' && toolName.trim() !== '')
    : [];
  const totalToolCalls = Number.isFinite(Number(progress.totalToolCalls)) ? Number(progress.totalToolCalls) : null;
  const elapsedMs = Number.isFinite(Number(progress.elapsedMs)) ? Number(progress.elapsedMs) : null;
  const toolCount = totalToolCalls ?? toolsCalled.length;
  const {
    progressMessage = '',
    progressMessageKey,
    progressMessageValues,
    isServerMessage,
  } = getLrrProgressMessageDescriptor(lrr);
  const steps = buildGenerationStepsFromProgress({
    completedTurns,
    totalTurns,
    toolsCalled,
    progressMessage,
    progressMessageKey,
    progressMessageValues,
    isInProgress: isInProgressLrrStatus(status),
  });

  return {
    status,
    progressMessage,
    progressMessageKey,
    progressMessageValues,
    ...(isServerMessage ? { isServerMessage: true } : {}),
    progress,
    steps,
    toolCount,
    toolsCalled,
    elapsedMs,
    completedTurns,
    totalTurns,
  };
};

export const buildGenerationStepsFromProgress = ({
  completedTurns,
  totalTurns,
  toolsCalled,
  progressMessage,
  progressMessageKey,
  progressMessageValues,
  isInProgress = false,
}) => {
  if (Number.isFinite(totalTurns) && totalTurns > 0) {
    const steps = [];

    for (let turnIndex = 1; turnIndex <= totalTurns; turnIndex += 1) {
      let state = STEP_STATE_PENDING;

      if (completedTurns !== null && turnIndex <= completedTurns) {
        state = STEP_STATE_DONE;
      } else if (completedTurns !== null && turnIndex === completedTurns + 1) {
        state = STEP_STATE_ACTIVE;
      } else if (completedTurns === null && turnIndex === 1) {
        state = STEP_STATE_ACTIVE;
      }

      const isActiveOrJustCompleted =
        state === STEP_STATE_ACTIVE ||
        (state === STEP_STATE_DONE && turnIndex === completedTurns && toolsCalled.length > 0);

      steps.push({
        id: `turn-${turnIndex}`,
        labelKey: 'command_intelligence_generation_turn_label',
        labelValues: { turn: turnIndex, total: totalTurns },
        state,
        ...(isActiveOrJustCompleted && toolsCalled.length > 0
          ? {
              detailKey: 'command_intelligence_generation_tools_used',
              detailValues: { tools: toolsCalled.join(', ') },
            }
          : {}),
      });
    }

    const hasActiveStep = steps.some(step => step.state === STEP_STATE_ACTIVE);

    if (isInProgress && !hasActiveStep) {
      steps.push({
        id: 'continuing',
        labelKey: 'command_intelligence_reasoning_thinking',
        state: STEP_STATE_ACTIVE,
      });
    }

    return steps;
  }

  if (progressMessage || progressMessageKey) {
    return [
      {
        id: 'progress-message',
        ...(progressMessageKey
          ? { labelKey: progressMessageKey, labelValues: progressMessageValues ?? {} }
          : { label: progressMessage }),
        state: STEP_STATE_ACTIVE,
      },
    ];
  }

  return THINKING_GENERATION_SNAPSHOT.steps;
};
